<template>
    <div class="objective-form">
        <h1>{{ $l10n.__('Créer un objectif') }}</h1>

        <div class="objective-form__wrapper">
            <preloader :display="loading" class="objective-form__loader" />
            <ObjectiveSectionDefine
                ref="objective-section-define"
                :key="`objective-section-define-${formKey}`"
                v-model="define"
                :settings="defineDatas"
                :errors="errors"
                :isDuplicateForm="isDuplicateForm"
                @update-rules="updateRules"
            />
            <ObjectiveSectionDuration
                ref="objective-section-duration"
                :key="`objective-section-duration-${formKey}`"
                v-model="duration"
                :settings="durationDatas"
                :errors="errors"
                @update-rules="updateRules"
            />
            <ObjectiveSectionResult
                ref="objective-section-result"
                :key="`objective-section-result-${formKey}`"
                v-model="result"
                :settings="resultDatas"
                :errors="errors"
                :selectedDuration="form.period"
                :periodDateFormat="periodDateFormat"
            />
        </div>
        <div class="objective-form-cta__container">
            <CompassButton
                :label="$t.__('common.actions.cancel')"
                :loading="loading"
                type="secondary"
                class="objective-form-cta__button cancel"
                @click="goToObjectivesListingPage"
            >
            </CompassButton>
            <CompassButton
                v-if="!isDuplicateForm"
                :label="$t.__('objectives.create.ObjectiveForm.save_and_create')"
                :loading="loading"
                type="secondary"
                class="objective-form-cta__button save-and-create"
                @click="validateAndSendForm(true)"
            >
            </CompassButton>
            <CompassButton
                :label="$t.__('common.actions.save')"
                :loading="loading"
                class="objective-form-cta__button save"
                @click="validateAndSendForm(false)"
            >
            </CompassButton>
        </div>
    </div>
</template>

<script>
import ObjectiveSectionDefine from './section/ObjectiveSectionDefine.vue';
import ObjectiveSectionDuration from './section/ObjectiveSectionDuration.vue';
import ObjectiveSectionResult from './section/ObjectiveSectionResult.vue';
import objectiveService from 'api/objectives/objectiveService';
import { fromSnakeToCamel } from 'Helpers/caseConversion';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import Schema from 'async-validator';
import FormMixins from 'mixins/FormMixins';
import { checkNotBlank } from 'vcomponents/forms/rules/Rules';
import { createNamespacedHelpers } from 'vuex';
import ObjectivesListing from 'stores/objectivesListing.js';

const {
    mapState: objectivesListingMapState,
    mapActions: objectivesListingMapActions
} = createNamespacedHelpers('objectivesListing');

export default {
    components: {
        ObjectiveSectionDefine,
        ObjectiveSectionDuration,
        ObjectiveSectionResult,
        Preloader,
    },
    mixins: [FormMixins],
    data() {
        return {
            formKey: 1,
            loading: true,
            debounceTimer: null,
            firstTimeComputeDuration: true,
            itemsType: 'product',
            currentCategory: 'turnover',
            form: {},
            rulesDictionnary: {
                itemId: {
                    message: this.$l10n.__('Veuillez renseigner un produit ou un service'),
                    validator: (rule, value, callback) => {
                        return !value || (value && value?.length === 0) ? false : callback();
                    },
                },
                itemCategoryId: {
                    message: this.$l10n.__('Veuillez renseigner une catégorie de produit'),
                    validator: (rule, value, callback) => {
                        return !value || (value && value?.length === 0) ? false : callback();
                    },
                },
                period: {
                    date_start: {
                        type: 'string',
                        message: this.$l10n.__('Veuillez renseigner une date'),
                        validator: checkNotBlank,
                    },
                },
                assignee: {
                    type: 'object',
                    fields: {
                        type: {
                            message: this.$l10n.__('Veuillez renseigner le responsable'),
                            validator: checkNotBlank,
                        },
                        id: {
                            message: this.$l10n.__('Veuillez renseigner le responsable'),
                            validator: checkNotBlank,
                        },
                    },
                },
            },
            rules: {
                name: {
                    type: 'string',
                    message: this.$l10n.__('Veuillez renseigner le nom de l’objectif'),
                    validator: checkNotBlank,
                },
                period: {
                    type: 'object',
                    fields: {
                        date_start: {
                            type: 'string',
                            message: this.$l10n.__('Veuillez renseigner une date'),
                            validator: (rule, value, callback) => {
                                return value?.length > 0 && value !== 'Invalid date'
                                    ? callback()
                                    : false;
                            },
                        },
                    },
                },
                turnover: {
                    type: 'object',
                    fields: {
                        amount: {
                            type: 'string',
                            message: this.$l10n.__('Veuillez renseigner le résultat attendu'),
                            validator: checkNotBlank,
                        },
                    },
                },
            },
            settings: {},
            upsellSettings: {},
            defineDatas: {
                categories: [],
                owners: [],
                itemCategories: [],
                metadata: {},
            },
            durationDatas: [],
            resultDatas: {
                previousRefinements: [],
                currency: '',
            },
            periodDateFormat: 'YYYY-MM-DD',
        };
    },
    computed: {
        ...objectivesListingMapState(['objectiveToDuplicate']),
        isDuplicateForm() {
            return this.objectiveToDuplicate !== null;
        },
        define: {
            get() {
                let itemId = null;
                if (this.itemsType === 'product') {
                    itemId = this.form.itemId;
                } else if (this.itemsType === 'category') {
                    itemId = parseInt(this.form.itemCategoryId);
                }
                return {
                    category: this.form.category,
                    name: this.form.name,
                    owner: this.form.assignee.type,
                    exactOwner: parseInt(this.form.assignee.id),
                    itemsType: this.itemsType,
                    itemName:
                        this.isDuplicateForm && this.objectiveToDuplicate.item
                            ? this.objectiveToDuplicate.item.name
                            : null,
                    itemId,
                    needUpdate: true,
                };
            },
            set(value) {
                this.form.name = value.name;
                this.form.assignee = {
                    type: value.owner,
                    id: value.exactOwner,
                };

                this.itemsType = value.itemsType;
                if (this.itemsType === 'product') {
                    this.form.itemCategoryId = null;
                    this.form.itemId = value.itemId;
                } else if (this.itemsType === 'category') {
                    this.form.itemId = null;
                    this.form.itemCategoryId = value.itemId;
                }

                if (value.categoryHasChanged) {
                    this.form.category = value.category;
                    this.currentCategory = value.category;
                    this.clearForm();
                }

                if (value.needUpdate) {
                    this.debounceUpdateResultDatas();
                }
            },
        },
        duration: {
            get() {
                return this.form.period;
            },
            set(value) {
                if (this.firstTimeComputeDuration) {
                    this.firstTimeComputeDuration = false;
                } else if (this.form.period.duration !== value.duration) {
                    this.$refs['objective-section-result'].isRefinementsAdjustable = false;
                    this.$refs['objective-section-result'].isRefinementsDisplayed = false;
                    this.$refs['objective-section-result'].expectedAmount = '';
                    this.$refs['objective-section-result'].resetRefinements();
                }

                this.form.period = value;
                this.debounceUpdateResultDatas();
            },
        },
        result: {
            get() {
                return {
                    turnover: this.form.turnover,
                    refinements: this.form.refinements,
                };
            },
            set(value) {
                this.form.turnover = value.turnover;
                this.form.refinements = value.refinements;
            },
        },
    },
    created() {
        if (!this.$store.state.objectivesListing) {
            this.$store.registerModule('objectivesListing', ObjectivesListing);
        }

        this.initFormData();
        EventBus.on('objectiveForm:resetExactOwner', this.resetExactOwner);
    },
    beforeUnmount() {
        EventBus.off('objectiveForm:resetExactOwner', this.resetExactOwner);
        this.clearObjectiveToDuplicateAction();

        if (this.$store.state.objectivesListing) {
            this.$store.unregisterModule('objectivesListing', ObjectivesListing);
        }
    },
    methods: {
        ...objectivesListingMapActions({
            clearObjectiveToDuplicateAction: 'clearObjectiveToDuplicateAction',
        }),
        resetExactOwner() {
            this.form.assignee.id = null;
            if (!this.defineDatas.owners.some((owner) => owner.values.length > 0)) {
                this.form.assignee.type = 'corp';
            }

            if (this.form.assignee.type !== 'corp') {
                const staffs = this.defineDatas.owners.find(
                    (ownerGroup) => ownerGroup.code === this.form.assignee.type,
                )?.values;
                if (staffs?.length > 0) {
                    this.form.assignee.id = parseInt(staffs[0].value);
                }
            }
        },
        updateDurationRule(rule) {
            if (this.form.period.duration === 'quarter') {
                this.rulesDictionnary.period.date_start.message = this.$l10n.__(
                    'Veuillez renseigner un trimestre',
                );
            } else {
                this.rulesDictionnary.period.date_start.message = this.$l10n.__(
                    'Veuillez renseigner une date',
                );
            }

            this.updateRule(rule, false);
        },
        updateRule(key, isDisabled) {
            const nodes = key?.split('.');
            const ruleName = nodes[0];

            if (isDisabled) {
                delete this.rules[ruleName];
            } else {
                if (nodes.length > 1) {
                    this.rules[ruleName].fields[nodes[1]] =
                        this.rulesDictionnary[ruleName][nodes[1]];
                } else {
                    this.rules[ruleName] = this.rulesDictionnary[ruleName];
                }
            }
        },
        updateRules(ruleData) {
            if (ruleData?.rule?.length > 0) {
                switch (ruleData.rule) {
                    case 'itemId':
                    case 'itemCategoryId':
                    case 'assignee':
                        this.updateRule(ruleData.rule, ruleData.disabled);
                        break;
                    case 'period.date_start':
                        this.updateDurationRule(ruleData.rule);
                        break;
                }
            }
        },
        updateResultDatas() {
            const payload = this.persistForm(this.form);
            const previousPeriod = {
                ...payload.period,
                date_start: moment(payload.period.date_start).subtract(1, 'year').format(),
                date_end: moment(payload.period.date_end).subtract(1, 'year').format(),
            };
            if (payload.assignee.type === 'corp' || payload.assignee.id > 0) {
                objectiveService
                    .getCompute(
                        payload.category,
                        payload.currency,
                        this.formatPeriod(previousPeriod),
                        payload.item_id,
                        payload.item_category_id,
                        payload.assignee,
                    )
                    .then((response) => {
                        this.resultDatas.previousRefinements = response.data;
                        this.resultDatas.currency = response.data[0].turnover.currency;
                    })
                    .catch((error) => {
                        console.error(error);
                    });
            }
        },
        validateAndSendForm(saveAndCreateNewObjective) {
            this.loading = true;
            const validator = new Schema(this.rules);
            this.errors.clearAll();

            validator
                .validate(this.form)
                .then(() => {
                    this.createNewObjective(saveAndCreateNewObjective);
                })
                .catch(({ errors }) => {
                    this.loading = false;
                    errors.forEach((error) => {
                        this.errors.setOneError(error.field, error.message);
                    });
                    errors.length > 0 ? this.scrollToError(errors[0].field) : this.scrollToTop();
                });
        },
        persistForm(data) {
            const payload = {
                category: data.category,
                currency: data.turnover.currency,
                period: data.period,
                item_id: data.itemId,
                item_category_id: data.itemCategoryId,
                assignee: {
                    type: data.assignee.type,
                },
            };

            if (payload.assignee.type !== 'corp') {
                payload.assignee.id = data.assignee.id;
            }

            return payload;
        },
        scrollToError(error) {
            const nodes = error?.split('.');
            let sectionKey;
            switch (nodes[0]) {
                case 'assignee':
                case 'name':
                case 'itemId':
                    sectionKey = 'define';
                    break;
                case 'turnover':
                case 'forecast_turnover':
                    sectionKey = 'result';
                    break;
                case 'period':
                    sectionKey = 'duration';
                    break;
                default:
                    sectionKey = nodes[0];
                    break;
            }

            if (sectionKey) {
                const ref = `objective-section-${sectionKey}`;
                this.$refs[ref].$el.scrollIntoView({
                    block: 'center',
                    behavior: 'smooth',
                    inline: 'end',
                });
            }
        },
        debounceUpdateResultDatas: function () {
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
            }
            const wait = 500;
            this.debounceTimer = setTimeout(() => {
                this.updateResultDatas();
            }, wait);
        },
        setDefineOwnersDatas() {
            const assigneesTypes = this.settings.data.assignees_types;
            const assigneesTypesUpsell = this.upsellSettings.data.assignees_types;
            const ownersDatas = [];
            for (const assigneeType of Object.values(assigneesTypes)) {
                const ownerDatas = {
                    code: assigneeType.value,
                    label: assigneeType.label,
                    order: assigneeType.order,
                    values: [],
                };

                switch (assigneeType.value) {
                    case 'staff':
                        ownerDatas.values = this.settings.data.assignees.staffs ?? [];
                        break;
                    case 'group':
                        ownerDatas.values = this.settings.data.assignees.groups ?? [];
                        break;
                    case 'corp':
                    default:
                        ownerDatas.values = [];
                        break;
                }

                if (assigneesTypesUpsell && assigneesTypesUpsell.length > 0) {
                    const ownerDatasUpsell = assigneesTypesUpsell.find(
                        (assigneeTypeUpsell) => assigneeTypeUpsell.value === ownerDatas.code,
                    );
                    if (ownerDatasUpsell) {
                        ownerDatas.upsell = ownerDatasUpsell.upsell;
                    }
                }

                ownersDatas.push(ownerDatas);
            }

            // add upsell owners if not found in assigneesTypes
            for (const assigneeTypesUpsell of Object.values(assigneesTypesUpsell)) {
                let ownerDatasFound;
                if (ownersDatas && ownersDatas.length > 0) {
                    ownerDatasFound = ownersDatas.find(
                        (ownerData) => ownerData.code === assigneeTypesUpsell.value,
                    );
                }

                if (!ownerDatasFound) {
                    ownersDatas.push({
                        code: assigneeTypesUpsell.value,
                        label: assigneeTypesUpsell.label,
                        order: assigneeTypesUpsell.order,
                        upsell: assigneeTypesUpsell.upsell,
                        values: [],
                    });
                }
            }

            if (
                ownersDatas.length > 0 &&
                ownersDatas.some((owner) => owner.upsell && owner.code === this.form.assignee.type)
            ) {
                const ownerWithoutUpsell = ownersDatas.find((owner) => !owner.upsell);
                if (ownerWithoutUpsell) {
                    this.form.assignee.type = ownerWithoutUpsell.code;
                }
            }

            ownersDatas.sort((ownerA, ownerB) => {
                if (ownerA.upsell && !ownerB.upsell) {
                    return 1;
                } else if (!ownerA.upsell && ownerB.upsell) {
                    return -1;
                }

                if (ownerA.order < ownerB.order) {
                    return -1;
                } else if (ownerA.order > ownerB.order) {
                    return 1;
                }

                return 0;
            });

            return ownersDatas;
        },
        setDefineMetadata() {
            const metadata = {};
            for (const [key, value] of Object.entries(this.settings.metadata)) {
                metadata[key] = {
                    value: value,
                };

                if (
                    this.upsellSettings &&
                    this.upsellSettings.metadata &&
                    this.upsellSettings.metadata[key]
                ) {
                    metadata[key] = {
                        ...metadata[key],
                        ...this.upsellSettings.metadata[key],
                    };
                }
            }

            return metadata;
        },
        async initFormData() {
            await this.initObjectiveForm();
            await this.getCurrencies();

            Promise.all([objectiveService.getMetaDatas(), objectiveService.getMetadatasUpsell()])
                .then(([metaDatas, upsellMetaDatas]) => {
                    const datas = Object.values(metaDatas.data);
                    this.settings = datas[0];
                    const upsellDatas = Object.values(upsellMetaDatas.data);
                    this.upsellSettings = upsellDatas[0];
                    this.form.category = this.isDuplicateForm
                        ? this.objectiveToDuplicate.category.code
                        : this.settings.data.category.code;
                    this.currentCategory = this.isDuplicateForm
                        ? this.objectiveToDuplicate.category.code
                        : this.settings.data.category.code;
                    this.defineDatas.categories = datas.map(
                        (objectiveType) => objectiveType.data.category,
                    );

                    this.defineDatas.itemCategories = this.settings.data.item_categories ?? [];
                    this.defineDatas.owners = this.setDefineOwnersDatas();
                    this.defineDatas.metadata = this.setDefineMetadata();
                    this.durationDatas = this.settings.data.durations;

                    if (!this.isDuplicateForm) {
                        this.resetExactOwner();
                    }
                    this.formKey++;

                    this.updateResultDatas();
                    this.updateRules({
                        rule: 'assignee',
                        disabled: this.form.assignee.type === 'corp',
                    });
                })
                .catch(() => {
                    Push.error(this.$l10n.__('Problème lors de la récupération des données'));
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        async createNewObjective(saveAndCreateNewObjective) {
            const formData = {
                name: this.form.name,
                category: this.form.category,
                assignee: {
                    type: this.form.assignee.type,
                },
                period: this.formatPeriod(this.form.period),
                item_id: this.form.itemId,
                item_category_id: this.form.itemCategoryId,
                turnover: {
                    ...this.form.turnover,
                    amount: this.form.turnover.amount.toString(),
                },
                refinements: this.form.refinements.map((refinement) => ({
                    period: this.formatPeriod(refinement.period),
                    turnover: {
                        ...refinement.turnover,
                        amount: refinement.turnover.amount.toString(),
                    },
                })),
            };

            if (formData.assignee.type !== 'corp' && this.form.assignee.id !== null) {
                formData.assignee.id = this.form.assignee.id;
            }

            await objectiveService
                .createObjective(formData)
                .then(() => {
                    saveAndCreateNewObjective ? this.clearForm() : this.goToObjectivesListingPage();
                    this.$pushToast({
                        type: 'success',
                        icon: true,
                        text: this.$l10n.__('Votre objectif a bien été créé'),
                    });
                })
                .catch((error) => {
                    this.loading = false;
                    if (error) {
                        const errorFields = Object.keys(error);
                        const errorValues = Object.values(error);

                        errorValues.forEach((errorValue, index) => {
                            this.errors.setOneError(
                                fromSnakeToCamel(errorFields[index]),
                                errorValue,
                            );
                        });
                    }
                    this.errors.length > 0
                        ? this.scrollToError(this.errors[0].field)
                        : this.scrollToTop();
                })
                .finally(() => {
                    this.scrollToTop();
                    this.loading = false;
                });
        },
        initObjectiveForm() {
            return new Promise((resolve) => {
                if (this.isDuplicateForm) {
                    this.itemsType = this.objectiveToDuplicate.item_category
                        ? 'category'
                        : 'product';
                    this.form = {
                        name: this.$t.__('objectives.create.ObjectiveForm.duplicate_name_label', {
                            objectiveName: this.objectiveToDuplicate.name,
                        }),
                        category: this.objectiveToDuplicate.category.code,
                        turnover: {
                            currency: this.objectiveToDuplicate.turnover.expected.currency,
                            amount: parseInt(this.objectiveToDuplicate.turnover.expected.amount),
                        },
                        period: this.objectiveToDuplicate.period,
                        itemId: this.objectiveToDuplicate.item?.id ?? null,
                        itemCategoryId: this.objectiveToDuplicate.item_category?.id ?? null,
                        refinements: this.objectiveToDuplicate.refinements.map((refinement) => ({
                            period: refinement.period,
                            turnover: {
                                currency: refinement.turnover.expected.currency,
                                amount: parseInt(refinement.turnover.expected.amount),
                            },
                        })),
                        assignee: {
                            type: this.objectiveToDuplicate.assignee.type,
                            id: this.objectiveToDuplicate.assignee.id,
                        },
                    };
                } else {
                    const defaultAssignee = {
                        type: 'staff',
                        id: 0,
                    };

                    if (this.defineDatas.owners.length > 0) {
                        defaultAssignee.type =
                            this.defineDatas.owners.find((owner) => !owner.upsell).code ?? 'corp';
                        defaultAssignee.id =
                            this.defineDatas.owners.find(
                                (owner) => owner.code === defaultAssignee.type,
                            )?.values[0]?.value ?? null;
                    }

                    this.form = {
                        name: '',
                        category: this.currentCategory,
                        turnover: {
                            currency: 'EUR',
                            amount: 0,
                        },
                        period: {
                            duration: 'year',
                            date_start: moment().startOf('year').format(),
                            date_end: moment().endOf('year').format(),
                        },
                        itemId: null,
                        itemCategoryId: null,
                        refinements: [],
                        assignee: defaultAssignee,
                    };
                }
                resolve();
            });
        },
        goToObjectivesListingPage() {
            this.$router.push({ path: '/objectives' });
        },
        async clearForm() {
            await this.initObjectiveForm();
            this.formKey++;
        },
        formatPeriod(period) {
            return {
                duration: period.duration === 'fiscal_year' ? 'year' : period.duration,
                date_start: moment(period['date_start']).format(this.periodDateFormat),
                date_end: moment(period['date_end']).format(this.periodDateFormat),
            };
        },
        async getCurrencies() {
            await this.$ajax.get('/rest/currency').then((response) => {
                const currentCurrency = response.data.find(
                    (currency) => currency.id === window.App.numformat.currencyid,
                );

                this.form.turnover.currency = currentCurrency?.name ?? 'EUR';
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.select-error {
    :deep() {
        .compass-form-item .compass-input {
            border-color: var(--colors-common-border-alert);
        }
    }
}

.objective-form {
    :deep(.select-error) {
        .el-date-editor .el-input__inner,
        .compass-input {
            border-color: var(--colors-common-border-alert);
        }
    }

    .select-error .el-date-editor.el-input .el-input__inner {
        border-color: var(--colors-common-border-alert);
    }

    &__wrapper {
        max-width: 650px;
        margin: 0 auto;
        padding-bottom: 100px;
    }

    &-cta {
        &__container {
            position: fixed;
            z-index: 2;
            right: 0;
            bottom: 0;

            display: flex;
            justify-content: end;

            width: 100%;
            padding-top: var(--spacing-3);
            padding-right: var(--spacing-5);
            padding-bottom: var(--spacing-3);

            background: var(--colors-common-background-default);
            box-shadow: 0 0 12px rgb(17 17 17 / 24%);

            :deep(.compass-button:not(:first-child)) {
                margin-left: var(--spacing-1);
            }
        }
    }

    &__loader {
        position: fixed;
    }
}
</style>

<style>
html > body div.global:has(.objective-form) {
    background-color: var(--colors-common-background-default);
}
</style>
